import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';

export interface MaterialParseEntry {
  id: string;
  file_name: string | null;
  title: string | null;
  parsed: boolean;
  hasFile: boolean;
}

interface Row {
  id: string;
  file_name: string | null;
  title: string | null;
  content: string | null;
  parsed_data: unknown | null;
}

async function fetchStatus(materialIds: string[]): Promise<MaterialParseEntry[]> {
  if (materialIds.length === 0) return [];
  const { data, error } = await supabase
    .schema('neurax' as never)
    .from('end_client_materials' as never)
    .select('id, file_name, title, content, parsed_data')
    .in('id', materialIds);

  if (error) throw new Error(`end_client_materials: ${error.message}`);

  return ((data ?? []) as unknown as Row[]).map((r) => {
    const hasFile = Boolean(r.file_name);
    // File rows are ready once parse-material-file has written the extracted
    // text. parsed_data only exists for some file types (resume DOCX/TXT get it,
    // other DOCX/TXT never do), and it is written in the same update as content,
    // so requiring it would stall the wizard forever. Text-only rows are pre-parsed.
    const parsed = hasFile ? Boolean(r.content) : true;
    return { id: r.id, file_name: r.file_name, title: r.title ?? null, parsed, hasFile };
  });
}

export function useMaterialsParseStatus(materialIds: string[]) {
  const enabled = materialIds.length > 0;

  const query = useQuery({
    queryKey: ['neurax', 'material-parse-status', [...materialIds].sort()],
    queryFn: () => fetchStatus(materialIds),
    enabled,
    refetchInterval: (q) => {
      const data = q.state.data as MaterialParseEntry[] | undefined;
      if (!data || data.length === 0) return false;
      // Stop polling once everything is parsed
      return data.every((d) => d.parsed) ? false : 3000;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
    retry: false,
  });

  const summary = useMemo(() => {
    const data = query.data ?? [];
    const parsedCount = data.filter((d) => d.parsed).length;
    const totalCount = data.length;
    const allParsed = enabled ? totalCount > 0 && data.every((d) => d.parsed) : true;
    return { parsedCount, totalCount, allParsed };
  }, [query.data, enabled]);

  return { ...query, ...summary };
}
